import { Exercise, SubmissionType, ReferenceType, ExerciseStatus } from "types";
import { GetSubmissionsOutput } from "server/StartupAdmins CRUD/OutputsTypes";

import { BASE_URL } from "server";

export async function getSchools() {
  try {
    console.log("Appel de l'API /schools...");
    const response = await fetch(`${BASE_URL}/schools`, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`);
      return [];
    }

    const data = await response.json();
    console.log("Données brutes reçues de l'API:", data);

    // Validation des données
    if (!Array.isArray(data)) {
      console.log("API returned non-array data:", data);
      return [];
    }

    console.log("Nombre total d'écoles reçues:", data.length);

    // Retourner directement le tableau des noms d'écoles
    const filteredSchools = data
      .filter((curr) => curr && curr.name && typeof curr.name === "string")
      .map((curr) => curr.name);

    console.log(
      "Écoles filtrées et mappées:",
      filteredSchools.length,
      filteredSchools,
    );
    return filteredSchools;
  } catch (error) {
    console.error("Error fetching schools:", error);
    return [];
  }
}

export function timeAgo(date: Date): string {
  const now = Date.now();
  const diffInSeconds = Math.floor((now - date.getTime()) / 1000);

  if (diffInSeconds < 1) {
    return "A l'instant";
  }

  if (diffInSeconds < 60) {
    return `Il y a ${diffInSeconds} s`;
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `Il y a ${diffInMinutes} min`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `Il y a ${diffInHours} h`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) {
    return `Il y a ${diffInDays} j`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return `Il y a ${diffInMonths} mois`;
  }

  const diffInYears = Math.floor(diffInMonths / 12);
  return `Il y a ${diffInYears} ans`;
}

export function userRoleDisplay(role: string): string {
  switch (role) {
    case "admin":
      return "Administrateur Startup";
    case "admin_school":
      return "Administrateur d'école";
    case "student":
      return "Élève";
    case "teacher":
      return "Enseignant";
    case "inspecteur":
      return "Inspecteur";
    case "parent":
      return "Parent";
    default:
      return "";
  }
}

export function highlightSearch(title: string, searchQuery: string) {
  if (!searchQuery) {
    return title;
  }

  const regex = new RegExp(`(${searchQuery})`, "gi");
  const parts = title.split(regex);

  return parts.map((part, index) =>
    regex.test(part) ? (
      <span key={index} className="bg-yellow-200">
        {part}
      </span>
    ) : (
      part
    ),
  );
}

export function formatDate(date: Date): string {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const isToday =
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();
  const isYesterday =
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();

  let formattedDate: string;

  if (isToday) {
    formattedDate = `Aujourd'hui, ${date
      .getHours()
      .toString()
      .padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
  } else if (isYesterday) {
    formattedDate = `Hier, ${date.getHours().toString().padStart(2, "0")}:${date
      .getMinutes()
      .toString()
      .padStart(2, "0")}`;
  } else {
    formattedDate = `${date.getDate().toString().padStart(2, "0")}/${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}/${date.getFullYear()}, ${date
      .getHours()
      .toString()
      .padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
  }

  return formattedDate;
}

export function highlightText(text: string, searchQuery: string) {
  if (!searchQuery) {
    return text;
  }

  const regex = new RegExp(`(${searchQuery})`, "gi");
  const parts = text.split(regex);

  return parts.map((part, index) =>
    regex.test(part) ? (
      <span key={index} className="bg-yellow-200">
        {part}
      </span>
    ) : (
      part
    ),
  );
}

// 1. Définitions de toutes les valeurs possibles pour SchoolClass.
export type SchoolClassValue =
  | "terminale_A1"
  | "terminale_A2"
  | "terminale_B"
  | "terminale_C"
  | "terminale_D"
  | "terminale_G1"
  | "terminale_G2"
  | "terminale_G3"
  | "troisieme_mc"
  | "troisieme_ml";

// 2. Mappage de value => label
const MAP_VALUE_TO_LABEL: Record<SchoolClassValue, string> = {
  terminale_A1: "Terminale A1",
  terminale_A2: "Terminale A2",
  terminale_B: "Terminale B",
  terminale_C: "Terminale C",
  terminale_D: "Terminale D",
  terminale_G1: "Terminale G1",
  terminale_G2: "Terminale G2",
  terminale_G3: "Terminale G3",
  troisieme_mc: "Troisième MC",
  troisieme_ml: "Troisième ML",
};

// 3. Fonction qui retourne le label pour une valeur donnée
export function getSchoolClassLabel(value?: string | null): string {
  if (!value) {
    return " ";
  }

  if ((value as SchoolClassValue) in MAP_VALUE_TO_LABEL) {
    return MAP_VALUE_TO_LABEL[value as SchoolClassValue];
  }

  return " ";
}

const MAP_SUBMISSION_TYPE_VALUE_TO_LABEL: Record<SubmissionType, string> = {
  control: "Contrôle",
  unclear: "Non démarré",
  blocage: "Blocage",
};

export function getSubmissionTypeLabel(value?: string | null): string {
  if (!value) {
    return " ";
  }

  if ((value as SubmissionType) in MAP_SUBMISSION_TYPE_VALUE_TO_LABEL) {
    return MAP_SUBMISSION_TYPE_VALUE_TO_LABEL[value as SubmissionType];
  }

  return " ";
}

// Mapping des matières vers leurs labels français
const MAP_MATIERE_VALUE_TO_LABEL: Record<string, string> = {
  mathematics: "Mathématiques",
  mathematiques: "Mathématiques",
  math: "Mathématiques",
  maths: "Mathématiques",
  physique: "Physique",
  physics: "Physique",
  chimie: "Chimie",
  chemistry: "Chimie",
  biologie: "Biologie",
  biology: "Biologie",
  histoire: "Histoire",
  history: "Histoire",
  geographie: "Géographie",
  geography: "Géographie",
  francais: "Français",
  français: "Français",
  anglais: "Anglais",
  english: "Anglais",
  philosophie: "Philosophie",
  philosophy: "Philosophie",
  economie: "Économie",
  economy: "Économie",
  sciences: "Sciences",
  science: "Sciences",
  informatique: "Informatique",
  computer_science: "Informatique",
  litterature: "Littérature",
  literature: "Littérature",
  espagnol: "Espagnol",
  spanish: "Espagnol",
  allemand: "Allemand",
  german: "Allemand",
  latin: "Latin",
  grec: "Grec",
  greek: "Grec",
  arts: "Arts",
  art: "Arts",
  musique: "Musique",
  music: "Musique",
  sport: "Sport",
  education_physique: "Éducation Physique",
  eps: "EPS",
  technologie: "Technologie",
  technology: "Technologie",
};

export function getMatiereLabel(value?: string | null): string {
  if (!value) {
    return "Non disponible";
  }

  const normalizedValue = value.toLowerCase().trim();

  // Vérifier d'abord la correspondance exacte
  if (normalizedValue in MAP_MATIERE_VALUE_TO_LABEL) {
    return MAP_MATIERE_VALUE_TO_LABEL[normalizedValue];
  }

  // Vérifier les correspondances partielles
  for (const [key, label] of Object.entries(MAP_MATIERE_VALUE_TO_LABEL)) {
    if (normalizedValue.includes(key) || key.includes(normalizedValue)) {
      return label;
    }
  }

  // Si aucune correspondance n'est trouvée, capitaliser la première lettre
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}
const MAP_EXERCISE_STATUS_VALUE_TO_LABEL: Record<ExerciseStatus, string> = {
  in_standby: "En attente",
  in_processing: "Assignée",
  resolved: "Terminée",
  rejected: "Rejetée",
};

export function getExerciseStatusLabel(value?: string | null): string {
  if (!value) {
    return " ";
  }

  if ((value as ExerciseStatus) in MAP_EXERCISE_STATUS_VALUE_TO_LABEL) {
    return MAP_EXERCISE_STATUS_VALUE_TO_LABEL[value as ExerciseStatus];
  }

  return " ";
}

export function exerciseTitle(
  exercise: GetSubmissionsOutput | Exercise,
): string {
  let title = getSubmissionTypeLabel(exercise.submissionType) + " : ";

  switch (exercise.referenceType) {
    case ReferenceType.LIVRE:
      title += `${exercise.referenceBook?.name}, page  ${exercise.referenceBook?.page}, exercice  n°${exercise.referenceBook?.exerciseNumber}`;
      break;
    case ReferenceType.EPREUVE:
      title += `Epreuve de ${exercise.referenceTest
        ?.session}, ${getSchoolClassLabel(
        exercise.referenceTest?.class,
      )} ${exercise.referenceTest?.schoolName}, année ${exercise.referenceTest
        ?.year}`;
      break;
    case ReferenceType.AUTRE:
      title += `${exercise.referenceOther?.info}`;
      break;
    default:
      title += "";
  }
  return title;
}
